import { ChangeEvent, useState, useContext } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import TextArea from 'react-textarea-autosize'
import {
  selectScheduleTourPhone,
  setScheduleTourQuestionsText,
  setScheduleTourStepAdvanceOneStep,
  selectScheduleTourStep,
} from '../../../../../../../../store/slices/scheduleTour'
import Text from '../../../../../../../shared/Text'
import { selectBuyerId } from '../../../../../../../../store/selectors/buyerInfo'
import {
  selectCustomerEmail,
  selectCustomerId,
  selectCustomerType,
  selectFullName,
} from '../../../../../../../../store/selectors/customerProfile'
import { selectListing } from '../../../../../../../../store/selectors/listings'
import {
  selectScheduleTourDate,
  selectScheduleTourIsInPerson,
  selectScheduleTourQuestionsText,
  setScheduleTourShowModal,
  setScheduleTourStep,
  STEPS,
  STEPS_CONST,
} from '../../../../../../../../store/slices/scheduleTour'
import Step from '../../../../../ModalStepsOutline'
import * as Sentry from '@sentry/browser'
import styles from './Questions.module.scss'
import { IconExclamationTriangle } from '../../../../../../../shared/Icon'
import { sendScheduleTourPostRequest } from '../../../../../../../../api/mikasa/requests'
import { CustomerType } from '../../../../../../../../types/customerType'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

export const Questions = () => {
  const dispatch = useDispatch()

  const questionsText = useSelector(selectScheduleTourQuestionsText)
  const tourDate = useSelector(selectScheduleTourDate)
  const inPerson = useSelector(selectScheduleTourIsInPerson)
  const name = useSelector(selectFullName)
  const email = useSelector(selectCustomerEmail)
  const tourPhone = useSelector(selectScheduleTourPhone)
  const listing = useSelector(selectListing)
  const homeBuyerId = useSelector(selectBuyerId)
  const customerId = useSelector(selectCustomerId)
  const customerType = useSelector(selectCustomerType)
  const [isLoading, setIsLoading] = useState(false)
  const [isApiError, setIsApiError] = useState(false)
  const currentStep = useSelector(selectScheduleTourStep)
  const stepIndex = STEPS.indexOf(currentStep)
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)
  const message = defineMessages({
    placeholder: {
      id: 'ScheduleTour.Questions.Placeholder',
      defaultMessage:
        'For example, we are available next week and are usually available after 6pm most days.',
    },
  })

  async function handleScheduleTourButtonClick() {
    try {
      setIsLoading(true)
      await sendScheduleTourPostRequest(
        {
          homeBuyerId,
          name: name || 'unknown',
          inPerson,
          tourDate,
          customerId,
          email,
          tourPhone,
          questionsText,
          listing,
        },
        ampli
      )
      dispatch(setScheduleTourStepAdvanceOneStep())
    } catch (e) {
      setIsApiError(true)
      Sentry.captureException(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Step stepLabel='schedule-tour-questions'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() => dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))}
      >
        <FormattedMessage
          id='ScheduleTour.stepProgressCounter.lastStep'
          defaultMessage='Last step'
        />
      </Step.HeaderNavSection>
      <Step.Title labelFor='contact-step-questions'>
        <FormattedMessage
          id='ScheduleTour.Questions.Label'
          defaultMessage='Do you have any questions for your {customerType}?'
          values={{
            customerType:
              customerType === CustomerType.LoanOfficers
                ? 'loan officer'
                : 'agent',
          }}
        />
      </Step.Title>
      <Step.Body>
        <div className={styles.bodyContents}>
          <TextArea
            id='contact-step-questions'
            value={questionsText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              if (e.target.value.length < 3000) {
                dispatch(setScheduleTourQuestionsText(e.target.value))
              }
            }}
            autoFocus
            minRows={6}
            maxRows={11}
            className={styles.textarea}
            placeholder={intl.formatMessage(message.placeholder)}
          />

          {isApiError ? (
            <div>
              <div>
                <IconExclamationTriangle className={styles.triangle} />
              </div>
              <Text
                className={styles.typicalResponseTimeText}
                variant='warning'
                variantCount='600'
                align='center'
                element='h3'
                weight='semibold'
                size='xxs'
              >
                <FormattedMessage
                  id='ScheduleTour.Questions.ApiError'
                  defaultMessage="Sorry about this, but something didn't work on our end. Can you try again?"
                />
              </Text>
            </div>
          ) : (
            <Text
              className={styles.typicalResponseTimeText}
              variant='neutralCool'
              variantCount='900'
              align='center'
              element='h3'
              weight='semibold'
              size='xxs'
            >
              <FormattedMessage
                id='ScheduleTour.Questions.TypicalResponseTime'
                defaultMessage='Typical response time is 24 to 48 hours'
              />
            </Text>
          )}
        </div>
      </Step.Body>
      <Step.FooterButton
        onClick={() => handleScheduleTourButtonClick()}
        loading={isLoading}
      >
        <FormattedMessage
          id='ScheduleTour.FooterButton.RequestTour'
          defaultMessage='Request Tour'
        />
      </Step.FooterButton>
    </Step>
  )
}
